<template>
  <div>
    <div class="section">
      <div class="container">
        <!-- Page Header -->
        <div class="mb-md">
          <div class="columns is-vcentered">
            <div class="column is-one-third">
              <breadcrumbs :history="breadcrumbs.history" :current="breadcrumbs.current"  />
            </div>
            <div class="column is-one-third">
              <h1 class="has-text-centered is-size-4">Edit Commission Profile</h1>
            </div>
            <div class="column is-one-third">
              <div class="is-pulled-right px-md">
                <b-tooltip v-if="connectionExists" label="Profile linked to a connection." position="is-bottom">
                  <b-button
                    class="mr-xl"
                    type="rounded-lg"
                    :disabled="connectionExists">
                    Delete
                  </b-button>
                </b-tooltip>
                <b-button
                  v-if="!connectionExists"
                  class="mr-xl"
                  type="rounded-lg"
                  :disabled="saving"
                  @click.prevent="showConfirmModal = true">
                  Delete
                </b-button>
                <b-button
                  type="is-primary rounded-lg"
                  :loading="saving"
                  :disabled="!isValid"
                  @click.prevent="updateProfile">
                  Update
                </b-button>
              </div>
            </div>
          </div>
        </div>
        <!-- Error Display -->
        <b-message v-if="!saving && 'non_field_errors' in errors" type="is-danger">
          {{ errors.non_field_errors[0] }}
        </b-message>
        <!-- loading -->
        <div v-if="loading">
          <loading message="Loading..."/>
        </div>
        <!-- Profile Info -->
        <div v-else-if="!loading && profile !== null" class="pb-lg mb-lg">
          <div class="columns">
            <div class="column is-one-fifth">
              <p class="has-text-weight-bold is-size-6 mb-sm">Profile Details</p>
              <p class="has-text-grey is-size-7">
                Configure basic settings for this commission profile.
              </p>
            </div>
            <div class="column">
              <ProfileInfo v-model="profile" :profileData="profile" :isEdit="true"/>
            </div>
          </div>
        </div>
        <div v-if="profile && profile.method === 'category'" class="columns">
          <div class="column is-one-fifth">
          <p class="has-text-weight-bold is-size-6 mb-sm">Categories and commission values</p>
          <p class="has-text-grey is-size-7">
            Details about categories and commission values.
          </p>
        </div>
        <div class="column">
          <CategoryInfo v-model="categoryDetails" :categoryDetails="categoryDetails" :profileData="profile" :isEdit="true" :selectedCats="selectedCats" @reload="load"/>
        </div>
        </div>
      </div>
    </div>
    <!-- show confirm modal -->
    <b-modal
      has-modal-card
      scroll="keep"
      :active.sync="showConfirmModal"
      :can-cancel="['x']"
      ><ConfirmationModal
        :label="'Delete Commission Profile'"
        :reason="'Are you sure you want to delete this profile? This action cannot be reversed.'"
        :confirmButtonText="'Delete'"
        :confirmButtonType="'is-danger'"
        :saving="deleting"
        @modal:confirm="deleteProfile"
        @modal:close="closeModal"
        />
    </b-modal>
  </div>
  </template>

<script>
import { mapGetters } from 'vuex'
import ConfirmationModal from '@/components/Modals/ConfirmationModal'
import ProfileInfo from '../components/Create/ProfileInfo'
import CategoryInfo from '../components/Create/CategoryInfo'

export default {
  components: {
    ConfirmationModal,
    ProfileInfo,
    CategoryInfo
  },
  computed: {
    ...mapGetters(['context']),
    endpoint () {
      return this.context.route + `/commission-profiles/${this.$route.params.id}/`
    },
    json () {
      const json = {
        name: this.profile.name,
        method: this.profile.method,
        description: this.profile.description,
        values: this.profile.values
      }
      if (this.profile.method === 'category') {
        const categoryValues = []
        this.categoryDetails.map(el => {
          if (el.retailer_category.category === 'All categories') {
            categoryValues.push({
              value: el.value / 100,
              start_at: el.start_at
            })
          } else {
            categoryValues.push({
              ...el,
              value: el.value / 100
            })
          }
          return categoryValues
        })
        json.values = categoryValues
      }
      return json
    },
    breadcrumbs () {
      return {
        history: [
          { label: 'Settings', name: 'settings' },
          { label: 'Commission Profiles', name: 'commission-profiles' }
        ],
        current: `#${this.$route.params.id}`
      }
    }
  },
  data () {
    return {
      profile: null,
      profileData: null,
      categoryDetails: [],
      showConfirmModal: false,
      saving: false,
      errors: {},
      loading: true,
      deleting: false,
      selectedCats: [],
      isValid: false,
      connectionExists: false
    }
  },
  methods: {
    load () {
      this.loading = true
      this.$http.get(this.endpoint).then(response => {
        this.profile = response.data
        this.profileData = response.data
        this.connectionExists = response.data.connections.length > 0
        if (this.profile.method === 'category') {
          const categories = []
          const categoryWithValues = []
          this.profile.values.map(el => {
            if (el.retailer_category) {
              categories.unshift(el.retailer_category)
              categoryWithValues.unshift({
                ...el,
                value: parseFloat(el.value) * 100
              })
            } else {
              categories.unshift({
                category: 'All categories'
              })
              categoryWithValues.unshift({
                ...el,
                value: parseFloat(el.value) * 100,
                retailer_category: {
                  category: 'All categories'
                }
              })
            }
            return categories
          })
          this.selectedCats = categories
          this.categoryDetails = categoryWithValues
        }
        this.loading = false
      })
    },
    deleteProfile () {
      this.deleting = true
      this.$http.delete(this.endpoint).then(response => {
        this.$buefy.toast.open({ type: 'is-success', message: 'Commission profile deleted!' })
        this.$router.push({ name: 'commission-profiles' })
        this.deleting = false
      })
    },
    updateProfile () {
      this.saving = true
      this.$http.patch(this.endpoint, this.json).then(response => {
        this.errors = {}
        this.$buefy.toast.open({ type: 'is-success', message: 'Commission profile updated!' })
        this.$router.push({ name: 'commission-profiles' })
      }).catch(error => {
        this.errors = (error.response.data) ? error.response.data : { detail: 'An unexpected error occurred!' }
      }).finally(() => {
        this.saving = false
      })
    },
    closeModal () {
      this.showConfirmModal = false
    },
    checkValidity () {
      this.isValid = this.json.name && this.json.method && this.json.values.every(item => item.start_at && item.value)
    }
  },
  mounted () {
    this.load()
  },
  watch: {
    profile () {
      this.checkValidity()
    },
    categoryDetails () {
      this.checkValidity()
    }
  }
}
</script>
